import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Navbar } from "@/components/site/Navbar";

import { Footer } from "@/components/site/Footer";
import { VisaTaxPackagesSection } from "@/components/site/VisaTaxWidget";
import { VisaTaxPopup } from "@/components/site/VisaTaxPopup";
import { SeoFaqBlock } from "@/components/site/SeoFaqBlock";
import { SEO_CONTENT } from "@/components/site/seo-content";
import { ServiceDetailSection } from "@/components/site/ServiceDetailSection";
import { SERVICE_DETAIL_CONTENT } from "@/lib/service-detail-content";

import { webPageScript } from "@/lib/webpage-jsonld";

export const Route = createFileRoute("/visa-tax")({
  component: VisaTaxPage,
  head: () => ({
    meta: [
      { title: "Visa Tax File Bangladesh | Online Tax VAT" },
      { name: "description", content: "Get a complete visa tax file for embassy applications — income tax return filing bangladesh, e-TIN registration bangladesh, NBR challan and tax certificate BD, prepared and delivered online." },
      { property: "og:title", content: "Visa Tax File Bangladesh | Income Tax Return for Embassy" },
      { property: "og:description", content: "Income tax return, e-TIN and NBR tax payment challan for visa applications — prepared online by NBR-experienced tax consultants in Bangladesh." },
      { property: "og:url", content: "https://onlinetaxvat.com/visa-tax" },
    ],
    links: [{ rel: "canonical", href: "https://onlinetaxvat.com/visa-tax" }],
    scripts: [webPageScript({
      path: "/visa-tax",
      name: "Visa Tax File — Online Tax VAT Bangladesh",
      description: "Visa tax file support for Bangladesh applicants, including tax return documents, challan support and delivery options.",
    })],
  }),
});

function VisaTaxPage() {
  const c = SEO_CONTENT["visa-tax"];
  const { i18n } = useTranslation();
  const isBn = (i18n.resolvedLanguage || i18n.language || "en").startsWith("bn");

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1">
        <h1 className="sr-only">Visa Tax File Packages in Bangladesh — 1, 2 & 3 Year Plans for Travel & Embassy Use</h1>
        <div
          style={{
            minHeight: 720,
            contentVisibility: "auto",
            containIntrinsicSize: "1px 720px",
          }}
        >
          <VisaTaxPackagesSection />
        </div>
        <div className="container-page pb-20" style={{ contain: "layout style" }}>

          <div className={`mt-10 max-w-3xl mx-auto text-sm leading-relaxed text-muted-foreground space-y-3 ${isBn ? "font-bn" : ""}`}>
            {isBn ? (
              <>
                <h2 className="text-center text-lg md:text-xl font-bold text-foreground">ভিসা ট্যাক্স ফাইল কী এবং কেন প্রয়োজন?</h2>
                <p className="text-center">
                  ভিসা ট্যাক্স ফাইল হলো ঢাকাস্থ বেশিরভাগ দূতাবাসের চাহিদা অনুযায়ী এনবিআর (NBR)-এর স্বীকৃতিপ্রাপ্ত নথিপত্রগুলোর একটি গোছানো বান্ডেল।
                </p>
                <p className="text-center">
                  এটি নিশ্চিত করে যে আবেদনকারী একজন প্রকৃত এবং কর-নিয়ম মেনে চলা বাংলাদেশী নাগরিক।
                </p>
                <p className="text-center">
                  অনলাইন ট্যাক্স ভ্যাট বাংলাদেশ এই ফাইলগুলো একদম শুরু থেকে শেষ পর্যন্ত সম্পূর্ণ প্রস্তুত করে দেয়।
                </p>
                <p className="text-center">
                  সমস্ত প্রয়োজনীয় কাগজপত্র একত্রিত করে একক একটি দূতাবাস-উপযোগী পিডিএফ (PDF) এবং প্রিন্টেড সেট তৈরি করা হয়, যার সাথে প্রয়োজন অনুযায়ী দ্বিভাষিক (বাংলা ও ইংরেজি) কাভার নোটও যুক্ত থাকে।
                </p>

                <p className="text-center font-medium text-foreground pt-2">ফাইলটিতে সাধারণত যা যা থাকে:</p>
                <ul className="list-disc pl-5 space-y-1 max-w-md mx-auto">
                  <li>ই-টিন (e-TIN) রেজিস্ট্রেশন সার্টিফিকেট</li>
                  <li>ফর্ম আইটি-১১জিএ (Form IT-11GA) অনুযায়ী আয়কর রিটার্ন</li>
                  <li>ট্যাক্স কম্পিউটেশন শিট বা কর হিসাবের বিবরণী</li>
                  <li>জমাকৃত চালানের কপি</li>
                  <li>অফিসিয়াল এনবিআর অ্যাকনলেজমেন্ট স্লিপ বা প্রাপ্তি স্বীকারপত্র</li>
                </ul>

                <h3 className="text-center text-base md:text-lg font-semibold text-foreground pt-4">আমাদের ভিসা ট্যাক্স প্যাকেজসমূহ</h3>
                <p className="text-center">
                  দূতাবাসের আয়কর ইতিহাস ও মেয়াদের প্রয়োজনীয়তা সহজে মেটানোর জন্য আমরা তিনটি স্ট্যান্ডার্ড প্যাকেজ অফার করে থাকি:
                </p>
                <ul className="list-disc pl-5 space-y-1.5 max-w-xl mx-auto">
                  <li><strong className="text-foreground">১ বছরের প্যাকেজ</strong> — প্রথমবার ভ্রমণকারী এবং স্বল্পমেয়াদী ভিসার আবেদনকারীদের জন্য উপযোগী।</li>
                  <li><strong className="text-foreground">২ বছরের প্যাকেজ</strong> — শেনজেন (Schengen), যুক্তরাজ্য, অস্ট্রেলিয়া, কানাডা এবং মধ্যপ্রাচ্যের ট্যুরিস্ট বা ভিজিট ভিসার জন্য সবচেয়ে বেশি চাহিদাপূর্ণ, যেখানে দূতাবাসগুলো একটি ধারাবাহিক আয়ের ইতিহাস দেখতে চায়।</li>
                  <li><strong className="text-foreground">৩ বছরের প্যাকেজ</strong> — স্টুডেন্ট ভিসা, ডিপেন্ডেন্ট ভিসা, মাল্টি-এন্ট্রি ভিসা এবং যাদের অতীতে অনিয়মিত ফাইল জমা দেওয়ার ইতিহাস রয়েছে তাদের জন্য বিশেষভাবে সুপারিশকৃত। অতিরিক্ত কর ধারাবাহিকতা ভিসার আবেদনকে আরও শক্তিশালী করে।</li>
                </ul>

                <h3 className="text-center text-base md:text-lg font-semibold text-foreground pt-4">কেন আমাদের সেবা গ্রহণ করবেন?</h3>
                <ul className="list-disc pl-5 space-y-1.5 max-w-xl mx-auto">
                  <li><strong className="text-foreground">এনবিআর-প্রত্যয়িত কনসালটেন্ট:</strong> সমস্ত ফাইল আপনার নিজস্ব টিনের (TIN) অধীনে এনবিআর-সার্টিফাইড কনসালটেন্টদের দ্বারা এবং আয়কর আইন মেনে প্রস্তুত করা হয়।</li>
                  <li><strong className="text-foreground">দ্রুত ডেলিভারি:</strong> বেশিরভাগ ক্ষেত্রেই একই দিনে ডেলিভারির সুবিধা রয়েছে। ঢাকার অফিস থেকে সরাসরি সংগ্রহ করতে পারেন।</li>
                  <li><strong className="text-foreground">ডিজিটাল ড্যাশবোর্ড ও পোর্টাল:</strong> ভিএফএস গ্লোবাল (VFS Global), বিএলএস ইন্টারন্যাশনাল (BLS International) এবং টিএলএস কন্টাক্ট (TLScontact)-এর মতো ভিসা পোর্টালের জন্য ডিজিটাল কপি আপনার সুরক্ষিত ক্লায়েন্ট ড্যাশবোর্ডে আপলোড করা হয়।</li>
                  <li><strong className="text-foreground">দীর্ঘস্থায়ী সুবিধা:</strong> জাতীয় রাজস্ব বোর্ড (NBR)-এর নিয়ম অনুযায়ী নির্ধারিত পূর্ণ সময় পর্যন্ত রিনিউয়াল, সংযোজন এবং রি-প্রিন্ট সুবিধা আমাদের পোর্টালে সুরক্ষিত থাকে।</li>
                </ul>
              </>
            ) : (
              <>
                <p className="text-center">
                  A Visa Tax File is the bundle of NBR-acknowledged documents most embassies in Dhaka request.
                  It confirms that a Bangladeshi applicant is a genuine, tax-compliant resident.
                </p>
                <p className="text-center">
                  Online Tax VAT Bangladesh prepares these files end-to-end. Everything is consolidated into a single, embassy-ready PDF and physical set, with bilingual cover notes where required.
                </p>
                <p className="text-center font-medium text-foreground">Each file typically includes:</p>
                <ul className="list-disc pl-5 space-y-1 max-w-md mx-auto">
                  <li>e-TIN registration</li>
                  <li>Income tax return on Form IT-11GA</li>
                  <li>Tax computation sheet</li>
                  <li>Paid challan</li>
                  <li>Official NBR acknowledgement slip</li>
                </ul>

                <p className="text-center pt-2">
                  We offer three standard packages so applicants can match the embassy&apos;s history requirement comfortably.
                </p>
                <ul className="list-disc pl-5 space-y-1.5 max-w-xl mx-auto">
                  <li><strong className="text-foreground">1-year package</strong> — suits first-time travellers and short-stay visas.</li>
                  <li><strong className="text-foreground">2-year package</strong> — the most common request for Schengen, UK, Australia, Canada and Middle-East tourist or visit visas, where consulates expect a consistent income history.</li>
                  <li><strong className="text-foreground">3-year package</strong> — recommended for student visas, dependant visas, multi-entry visas, and applicants with previously irregular filing where extra continuity strengthens the application.</li>
                </ul>

                <p className="text-center pt-2">
                  All filings are prepared by NBR-certified consultants under your own TIN and comply with the Income Tax Act.
                  Same-day delivery is available in many cases, with pickup from our Dhaka office.
                </p>
                <p className="text-center">
                  Digital copies are uploaded to your secure client dashboard for visa portals such as VFS Global, BLS International and TLScontact.
                  Renewals, addendums and re-prints remain accessible for the full retention period required by the National Board of Revenue.
                </p>
              </>
            )}
          </div>
          <SeoFaqBlock
            title={isBn ? "ভিসা ট্যাক্স ফাইল প্যাকেজ — দূতাবাস ও ট্রাভেল এজেন্সিগুলোর বিশ্বস্ত মাধ্যম" : c.title}
            intro={isBn
              ? "শেনজেন, যুক্তরাজ্য, যুক্তরাষ্ট্র, কানাডা, অস্ট্রেলিয়া এবং মধ্যপ্রাচ্যের দূতাবাসগুলোতে আবেদনকারীর জমা দেওয়া নথিপত্রের মধ্যে একটি পরিষ্কার ও সঠিকভাবে মূল্যায়িত ‘ভিসা ট্যাক্স ফাইল’ সবচেয়ে বেশি যাচাই করা হয়। আমাদের ১, ২ এবং ৩ বছরের প্যাকেজগুলোর মাধ্যমে টিন (TIN) সার্টিফিকেট, প্রাপ্তিস্বীকারপত্রসহ রিটার্ন, কম্পিউটেশন শিট এবং ব্যাংক চালান সংবলিত একটি পূর্ণাঙ্গ ট্যাক্স ফাইল প্রস্তুত করা হয় — যা আয়করের প্রমাণ চাওয়া প্রতিটি দূতাবাসেই গ্রহণযোগ্য।"
              : c.intro}
            bullets={isBn ? [
              "সম্পূর্ণ ট্যাক্স ফাইল: টিন (TIN), আইটি-১১জিএ (IT-11GA), কম্পিউটেশন, চালান এবং অ্যাকনলেজমেন্ট স্লিপ।",
              "আইনগত বৈধতা: আয়কর আইনের ১৭৫ ধারা অনুযায়ী ১, ২ এবং ৩ বছরের ব্যাকডেটেড ফাইল জমা দেওয়ার আইনগত সুযোগ রয়েছে।",
              "দূতাবাস-মানের প্রিন্টিং ও ডসিয়ার তৈরি: অন্তর্ভুক্ত রয়েছে প্রিমিয়াম মানের প্রিন্টিং ও গোছানো ফাইল সেট।",
              "জরুরি সেবা: জরুরি ভিসা ইন্টারভিউর জন্য একই দিনে ডেলিভারির সুবিধা।",
              "অনুবাদ সেবা: অনুরোধের ভিত্তিতে নোটারাইজড ইংরেজি অনুবাদ (সামান্য অতিরিক্ত ফি প্রযোজ্য)।",
              "কুরিয়ার সুবিধা: পুরো বাংলাদেশ জুড়ে এক্সপ্রেস কুরিয়ার এবং ঢাকার অফিসে সরাসরি সংগ্রহের সুবিধা।",
            ] : c.bullets}
            faqs={isBn ? [
              { q: "এই ট্যাক্স ফাইল কোন কোন দূতাবাস গ্রহণ করে?", a: "সকল শেনজেনভুক্ত দেশ, যুক্তরাজ্য, যুক্তরাষ্ট্র, কানাডা, অস্ট্রেলিয়া, সংযুক্ত আরব আমিরাত, সৌদি আরব, মালয়েশিয়া, সিঙ্গাপুর, জাপান, দক্ষিণ কোরিয়া, ভারতসহ প্রায় সব দূতাবাসই এনবিআর প্রাপ্তিস্বীকৃত রিটার্নকে আয়ের প্রমাণ হিসেবে গ্রহণ করে। আমরা ইতোমধ্যে ৩০টিরও বেশি দূতাবাসের জন্য ভিসা ফাইল প্রস্তুত করেছি।" },
              { q: "আমার কত বছরের রিটার্ন প্রয়োজন?", a: "স্বল্পমেয়াদী (ট্যুরিস্ট/বিজনেস) ভিসার ক্ষেত্রে সাধারণত ১ বছরের রিটার্নই যথেষ্ট। শেনজেন লং-স্টে, যুক্তরাজ্য ও যুক্তরাষ্ট্রের ভিসার জন্য সাধারণত ২–৩ বছরের রিটার্ন কাম্য। শক্তিশালী প্রোফাইলের জন্য আমরা ৩ বছরের প্যাকেজ সুপারিশ করি।" },
              { q: "ব্যাকডেটেড রিটার্ন জমা কি আইনসম্মত?", a: "হ্যাঁ। আয়কর আইন ২০২৩ অনুযায়ী প্রযোজ্য সারচার্জসহ বিলম্বিত রিটার্ন জমা দেওয়া সম্পূর্ণ বৈধ। আমাদের প্যাকেজ মূল্যের মধ্যেই সারচার্জ হিসাব ও প্রদানের গাইডলাইন অন্তর্ভুক্ত।" },
              { q: "কত দ্রুত ফাইল হাতে পাব?", a: "স্ট্যান্ডার্ড ডেলিভারি: ২–৩ কার্যদিবস। এক্সপ্রেস (অতিরিক্ত ফি প্রযোজ্য): ঢাকা সময় দুপুর ১:০০টার আগে অর্ডার করলে একই দিনে ডেলিভারি সম্ভব।" },
            ] : c.faqs}
          />
          <ServiceDetailSection content={SERVICE_DETAIL_CONTENT["visa-tax"]} />


        </div>

      </main>
      <Footer />
      <VisaTaxPopup />
    </div>
  );
}
